import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  PermissionGrantSummarySchema,
  type ApproverRuleDto,
  type MeResponse,
  type PermissionGrantSummaryDto,
  type WorkflowSubjectType,
} from '@taqon/contracts';
import { z } from 'zod';
import { ApiError, getData, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { ROLES, grantsKey } from './RolesPage';

/**
 * What a role needs before its holders can decide a step of a route (SDD §11.5; FRD-CORE-016):
 * view and approve on the record, and view and approve on approval steps. The route is checked
 * against the first when it is saved; the others decide whether a holder can open their inbox and
 * act. The Approval chart offers to add what is missing, through the same audited grant as the
 * Roles & permissions page: a route never grants anything by itself.
 */
export interface Needed {
  readonly recordType: string;
  readonly action: 'view' | 'approve';
  readonly text: string;
}

export interface RoleGap {
  readonly roleCode: string;
  readonly roleName: string;
  readonly privileged: boolean;
  readonly missing: readonly Needed[];
}

interface RoleLike {
  readonly code: string;
  readonly name: string;
  readonly isPrivileged?: boolean;
}

const STEPS = 'platform.approval_step';

export function neededFor(type: WorkflowSubjectType): readonly Needed[] {
  const noun = type.displayName;
  return [
    { recordType: type.recordType, action: 'approve', text: `approve ${noun} records` },
    { recordType: type.recordType, action: 'view', text: `view ${noun} records` },
    { recordType: STEPS, action: 'view', text: 'see approval steps in My approvals' },
    { recordType: STEPS, action: 'approve', text: 'decide approval steps' },
  ];
}

/** The roles a set of approver rules names (steps and escalation targets). */
export function rolesNamed(rules: readonly (ApproverRuleDto | null | undefined)[]): string[] {
  const codes = new Set<string>();
  for (const rule of rules) {
    if (rule !== null && rule !== undefined && rule.type !== 'reporting_manager') {
      codes.add(rule.roleCode);
    }
  }
  return [...codes];
}

/** A whole-record grant: own-records-only and field-level grants do not let anyone decide a step. */
export function covers(grants: readonly PermissionGrantSummaryDto[], need: Needed): boolean {
  return grants.some(
    (g) =>
      g.recordType === need.recordType &&
      g.action === need.action &&
      g.field === null &&
      !g.ownRecordsOnly,
  );
}

/** Whether this session may read and add role permissions, so the chart can offer to fix a gap. */
export function canGrant(session: MeResponse | null | undefined): boolean {
  return (
    hasCapability(session, 'platform.permission_grant:view') &&
    hasCapability(session, 'platform.permission_grant:create')
  );
}

const grantsOf = (roleCode: string) =>
  getData(
    `${ROLES}/${encodeURIComponent(roleCode)}/permission-grants`,
    z.array(PermissionGrantSummarySchema),
  );

/** What each named role still lacks to decide a step of this route. */
export async function gapsFor(
  queryClient: QueryClient,
  type: WorkflowSubjectType,
  roleCodes: readonly string[],
  roles: readonly RoleLike[],
): Promise<RoleGap[]> {
  const gaps: RoleGap[] = [];
  for (const code of roleCodes) {
    const grants = await queryClient.query({
      queryKey: grantsKey(code),
      queryFn: () => grantsOf(code),
    });
    const missing = neededFor(type).filter((need) => !covers(grants, need));
    if (missing.length === 0) continue;
    const role = roles.find((r) => r.code === code);
    gaps.push({
      roleCode: code,
      roleName: role?.name ?? code,
      privileged: role?.isPrivileged === true,
      missing,
    });
  }
  return gaps;
}

/**
 * The message for a route the API refused because a role cannot approve the record, for a session
 * that was not offered to fix it: it names the role and who can allow it. Any other failure keeps
 * its own message.
 */
export function refusalMessage(
  error: Error,
  type: WorkflowSubjectType,
  roles: readonly RoleLike[],
): string {
  if (!(error instanceof ApiError)) return error.message;
  const refused = (error.problem?.errors ?? []).filter((e) => e.rule === 'role_cannot_approve');
  if (refused.length === 0) return error.message;
  const names = refused.map((e) => {
    const code = /to (\S+) first$/.exec(e.message)?.[1] ?? '';
    return roles.find((r) => r.code === code)?.name ?? (code || 'A role');
  });
  return (
    `${[...new Set(names)].join(', ')} cannot approve ${type.displayName} records yet, so the route ` +
    'was not saved. An administrator who manages roles must allow it first, on Administration › ' +
    'Roles & permissions; or choose another role for the step.'
  );
}

/** A line under a chosen role when its holders could not yet decide the step. */
export function ApproverPermissionHint({
  rule,
  type,
  roles,
}: {
  rule: ApproverRuleDto | null;
  type: WorkflowSubjectType;
  roles: readonly RoleLike[];
}) {
  const session = useSession();
  const roleCode = rule !== null && rule.type !== 'reporting_manager' ? rule.roleCode : '';
  const allowed = canGrant(session.data);
  const grants = useQuery({
    queryKey: grantsKey(roleCode),
    queryFn: () => grantsOf(roleCode),
    enabled: roleCode !== '' && hasCapability(session.data, 'platform.permission_grant:view'),
    retry: false,
  });
  if (roleCode === '' || grants.data === undefined) return null;
  const approve = neededFor(type)[0];
  if (approve === undefined || covers(grants.data, approve)) return null;
  const role = roles.find((r) => r.code === roleCode);
  const name = role?.name ?? roleCode;
  return (
    <Typography variant="caption" color="warning.main" role="status">
      {name} cannot approve {type.displayName} records yet.{' '}
      {role?.isPrivileged === true
        ? 'It is a privileged administration role, so choose another role for this step.'
        : allowed
          ? 'When you save, you will be asked to allow it.'
          : 'An administrator who manages roles must allow it before the route can be saved.'}
    </Typography>
  );
}

/**
 * Confirms adding the missing permissions, then saves. A privileged administration role is never
 * given business approval here: it is changed, if at all, on the Roles & permissions page.
 */
export function AllowApproversDialog({
  gaps,
  type,
  onCancel,
  onAllowed,
}: {
  gaps: readonly RoleGap[];
  type: WorkflowSubjectType;
  onCancel: () => void;
  onAllowed: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const blocked = gaps.filter((g) => g.privileged);
  const allow = async () => {
    setBusy(true);
    setError(null);
    try {
      for (const gap of gaps) {
        for (const need of gap.missing) {
          await postJson(
            `${ROLES}/${encodeURIComponent(gap.roleCode)}/permission-grants`,
            { recordType: need.recordType, action: need.action },
            z.object({ id: z.string() }),
            session.data?.csrfToken ?? '',
          );
        }
        await queryClient.invalidateQueries({ queryKey: grantsKey(gap.roleCode) });
      }
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
      onAllowed();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog
      open
      title={blocked.length > 0 ? 'A role cannot approve here' : 'Allow these roles to approve'}
      message={
        blocked.length > 0
          ? `${blocked.map((g) => g.roleName).join(', ')} is a privileged administration role, so it is not given ${type.displayName} approvals from the Approval chart. Choose another role for the step.`
          : `Holders of these roles cannot yet decide this route's steps. Allowing it adds the permissions below to each role, exactly as on Roles & permissions. Every holder of the role gets them, for every ${type.displayName} they can see, and the change is recorded in the audit trail. Then the draft is saved.`
      }
      confirmLabel={blocked.length > 0 ? 'Close' : 'Allow and save'}
      tone="primary"
      busy={busy}
      error={error}
      onCancel={onCancel}
      onConfirm={() => {
        if (blocked.length > 0) onCancel();
        else void allow();
      }}
    >
      {blocked.length === 0 && (
        <Box component="ul" aria-label="Permissions to add" sx={{ mt: 2, mb: 0, pl: 2.5 }}>
          {gaps.map((gap) => (
            <li key={gap.roleCode}>
              <Typography variant="body2">
                <b>{gap.roleName}</b>: {gap.missing.map((m) => m.text).join(', ')}
              </Typography>
            </li>
          ))}
        </Box>
      )}
      {blocked.length === 0 && (
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          You can withdraw a permission later on Administration › Roles & permissions.
        </Alert>
      )}
    </ConfirmDialog>
  );
}
